import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const modeLabels = {
  online: 'Online',
  offline: 'Offline',
  hybrid: 'Online + Offline',
};

const statusStyles = {
  live: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  upcoming: 'bg-orange-50 text-[#C45A00] ring-orange-200',
  past: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const defaultAllowedCategories = [
  'Watercolor',
  'Acrylic',
  'Oil',
  'Pencil Sketching',
  'Charcoal',
  'Ink Art',
  'Handmade Canvas',
  'Mixed Media',
  'Digital Painting by Artist',
];

const defaultNotAllowed = [
  'Fully AI-generated artwork',
  'Copied, traced, stolen, or plagiarized artwork',
  'Adult, hateful, illegal, or misleading artwork',
  'AI images submitted as handmade artwork',
];

const defaultProofItems = [
  'Progress images',
  '10-30 second creation video',
  'Timelapse clip',
  'Brushwork or sketching clip',
  'Digital painting process screenshots',
  'Screen recording showing the artwork being created',
];

const defaultRecognitionTiers = [
  ['Top 25', 'Shortlisted Artists'],
  ['Top 10', 'Finalists'],
  ['Top 3', 'Grand Winners'],
  ['5-10', 'Special Mentions'],
];

const foundingPaintersRewards = [
  'Top three winners will receive Art Kits delivered directly to their provided address',
  'Featured placement on KalaManch.art',
  'Digital Winner Certificates',
  'Social media promotion across KalaManch platforms',
  'Founding Artist recognition',
  'Featured opportunities in upcoming KalaManch campaigns',
  'Priority showcase on KalaManch Android App',
];

const foundingPaintersPoster = '/images/competitions/founding-painters-challenge-2026.png';

const defaultHindiDetails = `थीम: "भारत आपकी नज़रों से"

KalaManch.art द्वारा आयोजित पहला ऑनलाइन पेंटिंग चैलेंज भारत भर के प्रतिभाशाली पेंटर्स और विज़ुअल आर्टिस्ट्स को खोजने, प्रमोट करने और मंच देने के लिए बनाया गया है।

भागीदारी पूरी तरह FREE है।

मान्य कला श्रेणियां:
- वॉटरकलर पेंटिंग
- एक्रिलिक पेंटिंग
- ऑयल पेंटिंग
- पेंसिल स्केचिंग
- चारकोल आर्ट
- इंक आर्ट
- हस्तनिर्मित मिक्स्ड मीडिया
- हैंडमेड कैनवास आर्टवर्क
- कलाकार द्वारा बनाई गई डिजिटल पेंटिंग

मान्य नहीं:
- पूरी तरह AI-generated artwork
- कॉपी, ट्रेस, चोरी या plagiarized artwork
- आपत्तिजनक, भ्रामक या गैरकानूनी सामग्री

शॉर्टलिस्टेड कलाकारों से creation proof मांगा जा सकता है। कला का स्वामित्व कलाकार के पास रहेगा।`;

const defaultSocialCaption = `Proud to participate in the KalaManch Founding Painters Challenge 2026.

Every artist deserves a stage.
Here is my artwork submission for the challenge.

I now nominate 5 fellow artists to showcase their talent on KalaManch.art:
@artist1 @artist2 @artist3 @artist4 @artist5

Join the movement:
https://www.kalamanch.art

#KalaManch #FoundingArtists #IndianArtists #PaintingCompetition #ArtistCommunity #HiddenTalentOfIndia #HarTalentKoManch`;

const emptySubmissionForm = {
  artwork_title: '',
  medium: '',
  category: '',
  description: '',
  artwork_image_url: '',
  process_video_url: '',
  progress_image_urls: '',
  instagram_url: '',
  referral_handles: '',
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const isFoundingPaintersCompetition = (competition) => /founding painters challenge 2026/i.test(competition?.name || '');

const competitionPoster = (competition) => (
  competition?.poster_url || (isFoundingPaintersCompetition(competition) ? foundingPaintersPoster : '') || competition?.image_url || ''
);

const CompetitionDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated: artistLoggedIn } = useArtistAuth();
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(searchParams.get('lang') === 'hi' ? 'hi' : 'en');
  const [submissionForm, setSubmissionForm] = useState(emptySubmissionForm);
  const [submissionBusy, setSubmissionBusy] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [uploadingArtwork, setUploadingArtwork] = useState(false);

  const allowedCategories = defaultAllowedCategories;
  const notAllowedItems = defaultNotAllowed;
  const proofItems = defaultProofItems;
  const recognitionTiers = defaultRecognitionTiers;

  useEffect(() => {
    let mounted = true;
    const loadCompetition = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/competitions`);
        if (!response.ok) throw new Error('Unable to load competitions');
        const data = await response.json();
        const match = asArray(data.competitions).find((item) => String(item.id) === String(id));
        if (mounted) setCompetition(match || null);
      } catch (error) {
        if (mounted) setCompetition(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadCompetition();
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!loading && window.location.hash === '#submit-artwork') {
      window.setTimeout(() => {
        scrollToSubmitArtwork();
      }, 100);
    }
  }, [loading]);

  const scrollToSubmitArtwork = () => {
    const target = document.getElementById('submit-artwork');
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - 112;
    window.history.replaceState(null, '', '#submit-artwork');
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  };

  const detailText = useMemo(() => {
    if (!competition) return '';
    if (language === 'hi') return competition.description_hi || (isFoundingPaintersCompetition(competition) ? defaultHindiDetails : competition.description || '');
    return competition.description || '';
  }, [competition, language]);

  const prizeText = useMemo(() => {
    if (!competition) return '';
    if (language === 'hi') return competition.prize_details_hi || competition.prize_details || '';
    return competition.prize_details || '';
  }, [competition, language]);

  const rewardItems = useMemo(() => {
    if (!competition) return [];
    const usesOldGenericRewardText = /Rewards may include digital certificates/i.test(competition.prize_details || '');
    if (isFoundingPaintersCompetition(competition) && usesOldGenericRewardText) return foundingPaintersRewards;
    const parsed = String(prizeText || '')
      .split(/\r?\n/)
      .map((item) => item.replace(/^[-*•\s]+/, '').trim())
      .filter((item) => item && !/^Recognition structure:?$/i.test(item));
    return parsed.length ? parsed : foundingPaintersRewards;
  }, [competition, prizeText]);

  const updateSubmissionField = (field, value) => {
    setSubmissionForm((current) => ({ ...current, [field]: value }));
  };

  const uploadArtworkImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingArtwork(true);
    setSubmissionError('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/project-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      updateSubmissionField('artwork_image_url', response.data.image_url || '');
    } catch (error) {
      setSubmissionError(error.response?.data?.detail || 'Unable to upload artwork image.');
    } finally {
      setUploadingArtwork(false);
      event.target.value = '';
    }
  };

  const submitArtworkEntry = async (event) => {
    event.preventDefault();
    if (!competition) return;
    if (!artistLoggedIn) {
      setSubmissionError('Login as an artist before submitting artwork.');
      window.dispatchEvent(new Event('openArtistJoin'));
      return;
    }
    setSubmissionBusy(true);
    setSubmissionError('');
    setSubmissionStatus('');
    try {
      const payload = {
        ...submissionForm,
        progress_image_urls: submissionForm.progress_image_urls
          .split(/\r?\n/)
          .map((item) => item.trim())
          .filter(Boolean),
        referral_handles: submissionForm.referral_handles
          .split(/\r?\n|,/)
          .map((item) => item.trim())
          .filter(Boolean),
      };
      await axios.post(`${API}/artist-auth/competitions/${competition.id}/submissions`, payload, { withCredentials: true });
      setSubmissionStatus('Artwork submitted. Admin will review it before it appears publicly.');
      setSubmissionForm(emptySubmissionForm);
    } catch (error) {
      setSubmissionError(error.response?.data?.detail || 'Unable to submit artwork right now.');
    } finally {
      setSubmissionBusy(false);
    }
  };

  if (loading) {
    return <main className="min-h-screen bg-[#F8FAFC] pt-32"><div className="mx-auto max-w-5xl px-4 text-slate-600">Loading competition...</div></main>;
  }

  if (!competition) {
    return (
      <main className="min-h-screen bg-[#F8FAFC] pt-32">
        <div className="mx-auto max-w-5xl px-4">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
            <h1 className="text-3xl font-black text-slate-950">Competition not found</h1>
            <Link to="/competitions" className="mt-5 inline-flex rounded-full bg-[#FF6F00] px-5 py-3 text-sm font-extrabold text-white">Back to competitions</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F8FAFC] pt-28 text-slate-950">
      <SEO
        path={`/competitions/${competition.id}`}
        title={`${competition.name} | KalaManch Competition`}
        description={competition.description || 'KalaManch competition details, rules, submissions, and artist showcase.'}
        keywords="KalaManch competition, artist competition India, competition submissions"
      />

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Link to="/competitions" className="text-sm font-black text-[#FF6F00] hover:text-[#C45A00]">Back to all competitions</Link>
        <div className="mt-5 overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-xl">
          {competitionPoster(competition) && (
            <div className="bg-slate-950 p-4 sm:p-6">
              <img src={competitionPoster(competition)} alt={competition.name} className="mx-auto max-h-[82vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl" />
            </div>
          )}
          <div className="p-6 sm:p-8 lg:p-10">
            <div className="flex flex-wrap gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] ring-1 ${statusStyles[competition.status] || statusStyles.upcoming}`}>
                {competition.status || 'upcoming'}
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                {modeLabels[competition.mode] || 'Online'}
              </span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <button type="button" onClick={() => setLanguage('en')} className={`rounded-full px-4 py-2 text-sm font-black ${language === 'en' ? 'bg-[#FF6F00] text-white' : 'border border-orange-200 text-[#C45A00]'}`}>English</button>
              <button type="button" onClick={() => setLanguage('hi')} className={`rounded-full px-4 py-2 text-sm font-black ${language === 'hi' ? 'bg-[#4A148C] text-white' : 'border border-purple-200 text-[#4A148C]'}`}>हिंदी</button>
            </div>
            <h1 className="mt-5 text-4xl font-black leading-tight sm:text-5xl">{competition.name}</h1>
            <div className="mt-4 grid gap-2 text-sm font-semibold text-slate-600 sm:grid-cols-2">
              {competition.competition_date && <p>Date: {competition.competition_date}</p>}
              {(competition.city || competition.venue) && <p>{[competition.venue, competition.city].filter(Boolean).join(', ')}</p>}
            </div>
            {detailText && <div className="mt-6 whitespace-pre-wrap rounded-3xl border border-slate-200 bg-slate-50 p-5 text-sm leading-7 text-slate-700">{detailText}</div>}
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={scrollToSubmitArtwork} className="rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 transition hover:-translate-y-0.5 hover:bg-[#E65F00]">Submit Artwork</button>
              {!artistLoggedIn && (
                <button type="button" onClick={() => window.dispatchEvent(new Event('openArtistJoin'))} className="rounded-full border border-[#FF6F00] px-6 py-3 text-sm font-extrabold text-[#C45A00] transition hover:-translate-y-0.5 hover:bg-orange-50">Artist Login / Registration</button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-5 lg:grid-cols-3">
          <article className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-sm lg:col-span-2">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-[#FF6F00]">Allowed categories</p>
            <div className="mt-5 flex flex-wrap gap-3">
              {allowedCategories.map((category) => (
                <span key={category} className="rounded-full bg-slate-100 px-4 py-2 text-sm font-extrabold text-slate-800">{category}</span>
              ))}
            </div>
            <div className="mt-6 rounded-3xl bg-emerald-50 p-5 text-sm leading-7 text-emerald-900">
              Traditional handmade art and digital painting created by the artist are allowed. Fully AI-generated artwork is not accepted for this competition.
            </div>
            <div className="mt-4 rounded-3xl bg-blue-50 p-5 text-sm leading-7 text-blue-950">
              Submitted artwork remains the property of the artist. KalaManch will not claim ownership or use entries for unrelated commercial purposes. Other participants and visitors must not copy, trace, reproduce, sell, or misuse any artist's work without permission.
            </div>
          </article>

          <article className="rounded-[2rem] border border-red-100 bg-white p-6 shadow-sm">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-red-600">Not allowed</p>
            <ul className="mt-4 space-y-3 text-sm font-semibold leading-6 text-slate-700">
              {notAllowedItems.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </article>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-emerald-100 bg-white shadow-sm">
          <div className="grid lg:grid-cols-[0.75fr_1.25fr]">
            <div className="bg-gradient-to-br from-emerald-700 via-[#0F766E] to-[#FF6F00] p-7 text-white sm:p-8">
              <p className="text-sm font-black uppercase tracking-[0.28em] text-emerald-100">Recognition & Rewards</p>
              <h2 className="mt-3 text-3xl font-black leading-tight">Winners will receive</h2>
              <p className="mt-4 text-sm font-semibold leading-6 text-white/82">
                Rewards are managed by KalaManch admin and may be updated for sponsors, certificates, app showcase, and final winner announcements.
              </p>
            </div>
            <div className="grid gap-3 p-5 sm:p-6 md:grid-cols-2">
              {rewardItems.map((reward) => (
                <div key={reward} className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold leading-6 text-emerald-950">
                  {reward}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-[#4A148C]">Recognition structure</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {recognitionTiers.map(([count, label]) => (
                <div key={label} className="rounded-3xl bg-slate-50 p-5">
                  <p className="text-3xl font-black text-slate-950">{count}</p>
                  <p className="mt-1 text-sm font-extrabold text-slate-600">{label}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-[#FF6F00]">Shortlisted verification</p>
            <h2 className="mt-3 text-2xl font-black text-slate-950">Creation proof required only after shortlisting.</h2>
            <p className="mt-3 text-sm leading-6 text-slate-700">To keep entry simple, creation proof is not required from every participant. Shortlisted artists must provide proof before final selection.</p>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              {proofItems.map((item) => <div key={item} className="rounded-2xl border border-orange-100 bg-orange-50/60 px-4 py-3 text-sm font-bold text-slate-800">{item}</div>)}
            </div>
          </article>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-5 lg:grid-cols-[1fr_0.8fr]">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-[#00A78E]">Artist referral bonus</p>
            <h2 className="mt-3 text-2xl font-black text-slate-950">Inviting artists is encouraged, not mandatory.</h2>
            <p className="mt-3 text-sm leading-6 text-slate-700">Artists who invite five fellow artists may be considered for featured profile chance, special community badge, homepage spotlight consideration, social repost opportunity, and founding artist community recognition. Referral recognition does not decide winners.</p>
          </article>
          <article className="rounded-[2rem] border border-slate-200 bg-slate-950 p-6 text-white shadow-sm">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-orange-200">Ready-to-use caption</p>
            <pre className="mt-4 whitespace-pre-wrap rounded-3xl bg-white/8 p-4 text-sm leading-6 text-white/88">{defaultSocialCaption}</pre>
          </article>
        </div>
      </section>

      <section id="submit-artwork" className="mx-auto max-w-7xl scroll-mt-28 px-4 pb-10 sm:px-6 lg:px-8">
        <form onSubmit={submitArtworkEntry} className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-xl sm:p-8">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.28em] text-[#00A78E]">Submit artwork</p>
              <h2 className="mt-2 text-3xl font-black text-slate-950">{competition.name}</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">Your entry will stay private until KalaManch admin reviews and approves it. Approved artwork appears publicly on this competition page.</p>
            </div>
          </div>

          {!artistLoggedIn && <div className="mt-5 rounded-3xl border border-orange-200 bg-orange-50 p-5 text-sm font-bold text-[#7C2D12]">Login as an artist to submit artwork. Competition browsing remains open to everyone.</div>}

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <input value={submissionForm.artwork_title} onChange={(event) => updateSubmissionField('artwork_title', event.target.value)} placeholder="Artwork title" className="rounded-2xl border border-slate-300 px-4 py-3" required />
            <input value={submissionForm.medium} onChange={(event) => updateSubmissionField('medium', event.target.value)} placeholder="Medium, e.g. watercolor, acrylic, digital painting" className="rounded-2xl border border-slate-300 px-4 py-3" required />
            <select value={submissionForm.category} onChange={(event) => updateSubmissionField('category', event.target.value)} className="rounded-2xl border border-slate-300 px-4 py-3">
              <option value="">Select category</option>
              {allowedCategories.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
            <input value={submissionForm.instagram_url} onChange={(event) => updateSubmissionField('instagram_url', event.target.value)} placeholder="Instagram post URL (optional)" className="rounded-2xl border border-slate-300 px-4 py-3" />
          </div>

          <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <label className="block text-sm font-black text-slate-900">Artwork image</label>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadArtworkImage} className="mt-2 w-full text-sm text-slate-700" disabled={!artistLoggedIn || uploadingArtwork} />
            <p className="mt-2 text-xs text-slate-500">Upload a clear JPG, PNG, or WebP artwork image.</p>
            {uploadingArtwork && <p className="mt-2 text-sm font-bold text-[#FF6F00]">Uploading artwork...</p>}
            <input value={submissionForm.artwork_image_url} onChange={(event) => updateSubmissionField('artwork_image_url', event.target.value)} placeholder="Artwork image URL appears here after upload" className="mt-3 w-full rounded-2xl border border-slate-300 px-4 py-3" required />
          </div>

          <textarea value={submissionForm.description} onChange={(event) => updateSubmissionField('description', event.target.value)} placeholder="Short artwork description or story behind the artwork" className="mt-4 min-h-[110px] w-full rounded-2xl border border-slate-300 px-4 py-3" />
          <textarea value={submissionForm.progress_image_urls} onChange={(event) => updateSubmissionField('progress_image_urls', event.target.value)} placeholder="Optional progress image URLs, one per line. Shortlisted artists may be asked for proof later." className="mt-4 min-h-[90px] w-full rounded-2xl border border-slate-300 px-4 py-3" />
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <input value={submissionForm.process_video_url} onChange={(event) => updateSubmissionField('process_video_url', event.target.value)} placeholder="Optional process video / timelapse URL" className="rounded-2xl border border-slate-300 px-4 py-3" />
            <input value={submissionForm.referral_handles} onChange={(event) => updateSubmissionField('referral_handles', event.target.value)} placeholder="Optional artist tags/referrals, comma separated" className="rounded-2xl border border-slate-300 px-4 py-3" />
          </div>

          {submissionError && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{submissionError}</p>}
          {submissionStatus && <p className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">{submissionStatus}</p>}

          <button type="submit" disabled={submissionBusy || !artistLoggedIn} className="mt-5 rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 disabled:cursor-not-allowed disabled:bg-slate-300">
            {submissionBusy ? 'Submitting...' : 'Submit for Admin Review'}
          </button>
        </form>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-black uppercase tracking-[0.24em] text-[#00A78E]">Approved artwork showcase</p>
          {competition.approved_submissions?.length > 0 ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {competition.approved_submissions.map((submission) => (
                <article key={submission.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <img src={submission.artwork_image_url} alt={submission.artwork_title} className="h-44 w-full object-cover" />
                  <div className="p-4">
                    <p className="font-black text-slate-950">{submission.artwork_title}</p>
                    <p className="mt-1 text-xs font-bold text-slate-600">{submission.artist_name} | {submission.medium}</p>
                    {submission.description && <p className="mt-2 text-sm leading-6 text-slate-600">{submission.description}</p>}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-3xl border border-dashed border-emerald-200 bg-emerald-50/40 p-6 text-sm font-semibold text-emerald-900">Approved submissions and winners for this competition will appear here after admin review.</div>
          )}
        </div>
      </section>
    </main>
  );
};

export default CompetitionDetail;
